import React from 'react'
import _get from 'lodash/get'
import _set from 'lodash/set'
import { List, Map, fromJS } from 'immutable'
import { useQuery } from '@apollo/client'
import {
  CONTENT_IMAGE_TYPE_POSTER_3X4,
  getOrderedContent,
  getFormattedContentData,
} from 'services/content'
import { GET_CONTENT } from 'services/content/query'
import { APOLLO_CLIENT_FETCH_POLICY_CACHE_FIRST } from 'services/apollo-graphql'
import Picture, { IMAGE_TYPE_WEBP, IMAGE_LOADING_LAZY } from 'components/Picture'
import { get as getConfig } from 'config'
import {
  parse as parseQuery,
  stringify as stringifyQuery,
} from 'services/query-string'

const config = getConfig()
const optimizedImagesEnabled = !!_get(config, ['features', 'optimizedImages'])
const MAX_ROW_ITEMS = 7

/**
 * The share page tile component
 * @param {Object} props - The props
 * @param {Map} props.data - The tile item data
 * @returns {JSX.Element} The tile component
 */
function SharePageV3Tile (props) {
  const { data } = props

  const title = data.get('title', '')
  const images = data.get('images', List()) || List()
  const imageData = images.find((img) => img.get('type') === CONTENT_IMAGE_TYPE_POSTER_3X4) || Map()
  const image = imageData.get('url', '')
  const imageWidth = '385'
  const imageHeight = '513'
  let imageUrl = ''
  let fallbackImageUrl = ''

  try {
    if (optimizedImagesEnabled) {
      const fallbackImageUrlObject = new URL(image)
      const imageUrlObject = new URL(image)
      const parsedFallbackImageQuery = parseQuery(_get(fallbackImageUrlObject, 'search', '')) || {}
      const parsedImageQuery = parseQuery(_get(imageUrlObject, 'search', '')) || {}
      const fallbackImageOptions = {
        ...parsedFallbackImageQuery,
        w: imageWidth,
        h: imageHeight,
        fm: 'jpg',
        compress: 'auto',
      }
      const imageOptions = {
        ...parsedImageQuery,
        w: imageWidth,
        h: imageHeight,
        fm: 'webp',
        q: '100',
      }

      const stringifiedFallbackQuery = stringifyQuery(fallbackImageOptions)
      const stringifiedImageQuery = stringifyQuery(imageOptions)

      // set the search string back onto the fallback URL
      _set(fallbackImageUrlObject, 'search', stringifiedFallbackQuery)

      // set the search string back onto the image URL
      _set(imageUrlObject, 'search', stringifiedImageQuery)

      fallbackImageUrl = fallbackImageUrlObject.toString()
      imageUrl = imageUrlObject.toString()
    }
  } catch (e) {
    fallbackImageUrl = image
    imageUrl = image
  }

  return (
    <div className="share-page-v3__tile">
      <div className="share-page-v3__tile-hero">
        {
          optimizedImagesEnabled
            ? <Picture
              fallbackSrc={fallbackImageUrl}
              width={imageWidth}
              height={imageHeight}
              alt={title}
              loading={IMAGE_LOADING_LAZY}
              sources={[
                {
                  type: IMAGE_TYPE_WEBP,
                  srcSet: imageUrl,
                },
              ]}
            />
            : <img
              src={image}
              loading="lazy"
              alt={title}
            />
        }
      </div>
    </div>
  )
}

/**
 * The share tiles component
 * @param {String} props.language - The user's language
 * @param {Array} props.titleGroupIds - The array of title group ids
 * @returns {JSX.Element} tiles component
 */
function SharePageV3Tiles (props) {
  const {
    language,
    titleGroupIds,
  } = props

  const { data, loading } = useQuery(GET_CONTENT, {
    variables: {
      language: [language, '_primary'],
      primaryLanguage: [language],
      titleGroupIds,
      imageType: [CONTENT_IMAGE_TYPE_POSTER_3X4],
      limitedData: true,
    },
    fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_CACHE_FIRST,
  })

  const contentList = _get(data, ['contentList', 'content'], []) || []
  const orderedList = getOrderedContent(titleGroupIds, contentList, 'titleGroupId')
  const immutableContentList = fromJS(orderedList.slice(0, MAX_ROW_ITEMS)) || List()

  return (
    <div className="share-page-v3__tiles">
      <div className="share-page-v3__tiles-row">
        <div className="share-page-v3__tiles-row-inner">
          {loading
            ? titleGroupIds.slice(0, MAX_ROW_ITEMS).map((id) => (
              <div key={id} className="share-page-v3__tiles-row-item">
                <div className="share-page-v3__tile-placeholder" />
              </div>
            ))
            : immutableContentList.map((item) => (
              <div key={item.get('id')} className="share-page-v3__tiles-row-item">
                <SharePageV3Tile data={getFormattedContentData(item)} />
              </div>
            ))
          }
        </div>
      </div>
    </div>
  )
}

export default SharePageV3Tiles
